import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Tooltip } from 'antd';

import RecordBadge from '../common/RecordBadge';
import EventFormElement from '../events/EventFormElement';
import { formatValue } from '../items/itemValueDisplay';
import { ARROW_RIGHT, LOCK } from '../icons';
import { elementLabel } from './changeCardModel';
import {
	ALWAYS_RUNS,
	ALWAYS_RUNS_WHY,
	BACKGROUND_LINE,
	BLOCKED_TITLE,
	CHECK_AGAIN,
	DEPENDENTS_TITLE,
	GIVE_VALUE_TITLE,
	LEFT_BEHIND_PREFIX,
	NOT_CHOOSABLE_WHY,
	PREVIEW_INTRO,
	PREVIEW_TITLE,
	RULE_NOT_MANAGED,
	WILL_NOT_MOVE_PREFIX,
	blockedBody,
	dependentRows,
	dependentsIntro,
	failingLead,
	giveValueIntro,
	hiddenLookupLine,
	itemsText,
	keeprWritesLine,
	lookupLine,
	matchedLine,
	moreLine,
	movingLine,
	noDependents,
	noRules,
	previewAfter,
	previewNow,
	rulesIntro,
	rulesTitle,
	skippedLead,
	skippedRows,
	skippedTotal,
	sourceRowParts,
	sourcesTitle,
	warningText,
} from './reviewCopy';

/*
 * The review step's panels (KPE-028, KPR-709): each one part of the dry
 * run's answer, in the words of reviewCopy.js. Presentational only; the
 * step (ReviewStep.js) owns the state.
 */

const Panel = ({ title, label, className = '', children }) => (
	<section className={`change-card__panel change-card__review-panel ${className}`.trim()} aria-label={label || title}>
		{title ? <h2 className="change-card__panel-title">{title}</h2> : null}
		{children}
	</section>
);

const join = parts => parts.filter(Boolean).join(' · ');

/** 1. What will move: the counts, the failures and the skips. */
export function NumbersPanel({ data, ctx }) {
	const items = data?.items || {};
	const failing = Number(items.failing) || 0;
	const skipped = skippedTotal(items.skipped);
	const failures = (data?.targets || []).flatMap(t => (t.failures || []).map(f => ({ ...f, element: t.element })));
	const listed = failures.reduce((sum, f) => sum + (Number(f.count) || 0), 0);
	return (
		<Panel title="What will move" className="change-card__numbers">
			<p className="change-card__lead">{movingLine(items, ctx)}</p>
			{failing ? (
				<>
					<p className="change-card__note">{failingLead(failing)}</p>
					<ul className="change-card__review-list">
						{failures.map(f => (
							<li key={`${f.element}:${f.code}`}>{f.message || f.code}{Number(f.count) > 1 ? ` (${itemsText(f.count)})` : ''}</li>
						))}
						{failing > listed ? <li className="change-card__muted">{moreLine(failing - listed)}</li> : null}
					</ul>
				</>
			) : null}
			{skipped ? (
				<>
					<p className="change-card__note">{skippedLead(skipped, ctx)}</p>
					<ul className="change-card__review-list">
						{skippedRows(items.skipped, ctx).map(row => (
							<li key={row.reason}>{itemsText(row.count)} — {row.words}</li>
						))}
					</ul>
				</>
			) : null}
			{data?.runsAs === 'background' ? <p className="change-card__note">{BACKGROUND_LINE}</p> : null}
		</Panel>
	);
}

/** 2. Where each element goes. */
export function SourcesPanel({ data, ctx }) {
	const rows = data?.sources || [];
	if (!rows.length) return null;
	return (
		<Panel title={sourcesTitle(ctx)} className="change-card__sources-review">
			<ul className="change-card__review-list change-card__review-list--plain">
				{rows.map(row => {
					const { label, goesTo, meta } = sourceRowParts(row, ctx);
					return (
						<li key={row.element} className="change-card__source-line">
							<span className="change-card__source-name">{label}</span>
							{goesTo.length ? (
								<>
									<ARROW_RIGHT aria-hidden="true" className="change-card__arrow" />
									<span className="change-card__sr">goes to</span>
									<span className="change-card__source-name">{goesTo.join(', ')}</span>
								</>
							) : null}
							<span className="change-card__left-meta">{join(meta)}</span>
						</li>
					);
				})}
			</ul>
		</Panel>
	);
}

/** 4. The warnings: data kept in History only, and what else to know. */
export function WarningsPanel({ warnings = [], ctx }) {
	const shown = (warnings || []).map(w => warningText(w, ctx)).filter(w => w.text);
	if (!shown.length) return null;
	return (
		<div className="change-card__warnings">
			{shown.map(w => (
				<Alert
					key={w.code}
					type="warning"
					showIcon
					className="change-card__warning"
					message={w.text}
					description={w.link?.to ? <Link to={w.link.to}>{w.link.label}</Link> : null}
				/>
			))}
		</div>
	);
}

/** 5. A value for each required target nothing maps. */
export function GiveValuePanel({ elements = [], values = {}, errors = {}, onChange, ctx }) {
	if (!elements.length) return null;
	return (
		<Panel title={GIVE_VALUE_TITLE} className="change-card__give">
			<p className="change-card__note">{giveValueIntro(ctx)}</p>
			{elements.map(el => (
				<div key={el.name} className="change-card__give-field">
					<EventFormElement
						event={{ _id: null, elements: values }}
						element={el}
						value={values[el.name]}
						onChange={onChange}
						showIconBadge={false}
						errorMessage={errors[el.name] || null}
					/>
				</div>
			))}
		</Panel>
	);
}

/** 6. Lookups elsewhere that stop the move until they are changed. */
export function BlockedPanel({ refusal, ctx, cardsById, onRetry, retrying }) {
	const lookups = refusal?.lookups || [];
	const hidden = Number(refusal?.hiddenItems) || 0;
	return (
		<Alert
			type="error"
			showIcon
			className="change-card__blocked"
			message={BLOCKED_TITLE}
			description={(
				<>
					<p className="change-card__note">{blockedBody(ctx)}</p>
					<ul className="change-card__review-list change-card__review-list--plain">
						{lookups.map(lookup => {
							const record = (lookup.card?.id && cardsById?.[lookup.card.id]) || { name: lookup.card?.name };
							return (
								<li key={`${lookup.card?.id}:${lookup.element}:${lookup.column || ''}`} className="change-card__lookup-line">
									<RecordBadge kind="card" record={record} title={record.name || lookup.card?.name || 'A card'} size="xs" />
									<span>{lookupLine(lookup)}</span>
								</li>
							);
						})}
						{hidden ? <li>{hiddenLookupLine(hidden)}</li> : null}
					</ul>
				</>
			)}
			action={<Button size="small" onClick={onRetry} loading={retrying}>{CHECK_AGAIN}</Button>}
		/>
	);
}

/** 7. What names the source card, and stops applying to the moved items. */
export function DependentsPanel({ dependents, ctx }) {
	const rows = dependentRows(dependents?.dependents || {});
	return (
		<Panel title={DEPENDENTS_TITLE} className="change-card__dependents">
			{rows.length ? (
				<>
					<p className="change-card__note">{dependentsIntro(ctx)}</p>
					<ul className="change-card__review-list change-card__review-list--plain">
						{rows.map(row => (
							<li key={row.kind}>
								<strong>{row.label}</strong>
								<span className="change-card__left-meta">{join([String(row.count.toLocaleString('en-US')), row.names, row.hidden])}</span>
							</li>
						))}
					</ul>
				</>
			) : (
				<p className="change-card__note">{noDependents(ctx)}</p>
			)}
		</Panel>
	);
}

/** 8. The target card's rules on an added item, ticked as the person chooses. */
export function RulesPanel({ rules = [], keeprWrites = [], onTick, ctx }) {
	const writes = keeprWritesLine(keeprWrites);
	return (
		<Panel title={rulesTitle(ctx)} className="change-card__rules">
			{rules.length ? (
				<>
					<p className="change-card__note">{rulesIntro(ctx)}</p>
					<ul className="change-card__review-list change-card__review-list--plain">
						{rules.map((rule, index) => {
							const name = rule.name || RULE_NOT_MANAGED;
							const locked = rule.alwaysRun || !rule.choosable;
							const why = rule.alwaysRun ? ALWAYS_RUNS_WHY : (!rule.choosable ? NOT_CHOOSABLE_WHY : null);
							const box = (
								<Checkbox
									checked={!!rule.ticked}
									disabled={locked}
									onChange={e => onTick(rule.id, e.target.checked)}
									aria-label={`Run ${name}`}
								>
									<span className="change-card__rule-name">{name}</span>
								</Checkbox>
							);
							return (
								<li key={rule.id || `rule-${index}`} className="change-card__rule-line">
									{why ? <Tooltip title={why}>{box}</Tooltip> : box}
									<span className="change-card__left-meta">
										{join([(rule.does || []).join(', '), matchedLine(rule.matched)])}
									</span>
									{rule.alwaysRun ? (
										<span className="change-card__left-meta"><LOCK aria-hidden="true" /> {ALWAYS_RUNS}</span>
									) : null}
								</li>
							);
						})}
					</ul>
				</>
			) : (
				<p className="change-card__note">{noRules(ctx)}</p>
			)}
			{writes ? <p className="change-card__note">{writes}</p> : null}
		</Panel>
	);
}

const valueOf = (element, raw) => {
	if (raw === null || raw === undefined || raw === '') return null;
	if (element) {
		const text = formatValue(element, raw);
		if (text !== '' && text !== null && text !== undefined) return text;
	}
	if (Array.isArray(raw)) return raw.join(', ');
	if (typeof raw === 'object') return JSON.stringify(raw);
	return String(raw);
};

function ValueList({ values = {}, elementsByName = {} }) {
	const rows = Object.keys(values || {})
		.map(name => ({ name, text: valueOf(elementsByName[name], values[name]) }))
		.filter(row => row.text !== null);
	if (!rows.length) return <p className="change-card__muted">—</p>;
	return (
		<dl className="change-card__values">
			{rows.map(row => (
				<div key={row.name} className="change-card__value-row">
					<dt>{elementsByName[row.name] ? elementLabel(elementsByName[row.name]) : row.name}</dt>
					<dd>{row.text}</dd>
				</div>
			))}
		</dl>
	);
}

/** 9. A few real items, before and after. */
export function PreviewPanel({ preview = [], ctx, sourcesByName = {}, targetsByName = {} }) {
	if (!preview.length) return null;
	return (
		<Panel title={PREVIEW_TITLE} className="change-card__preview">
			<p className="change-card__note">{PREVIEW_INTRO}</p>
			<ol className="change-card__preview-list">
				{preview.map(item => {
					const left = Object.keys(item.unmapped || {})
						.map(name => {
							const text = valueOf(sourcesByName[name], item.unmapped[name]);
							return text === null ? null : `${sourcesByName[name] ? elementLabel(sourcesByName[name]) : name}: ${text}`;
						})
						.filter(Boolean);
					return (
						<li key={item.id} className="change-card__preview-item">
							<h3 className="change-card__preview-title">{item.title || item.id}</h3>
							<div className="change-card__preview-grid">
								<div>
									<p className="change-card__preview-side">{previewNow(ctx)}</p>
									<ValueList values={item.before} elementsByName={sourcesByName} />
								</div>
								<div>
									<p className="change-card__preview-side">{previewAfter(ctx)}</p>
									<ValueList values={item.after} elementsByName={targetsByName} />
								</div>
							</div>
							{left.length ? <p className="change-card__note">{LEFT_BEHIND_PREFIX} {left.join('; ')}</p> : null}
							{item.moves === false && item.failures?.length ? (
								<p className="change-card__note change-card__danger">
									{WILL_NOT_MOVE_PREFIX} {item.failures.map(f => f.message || f.code).join(' ')}
								</p>
							) : null}
						</li>
					);
				})}
			</ol>
		</Panel>
	);
}
